/**
 * English message tree. `dv.ts` must carry the exact same key shape —
 * completeness.spec.ts enforces it mechanically.
 */
const en = {
  app: {
    name: 'Masveriya',
  },
  common: {
    loading: 'Loading…',
    retry: 'Try again',
    save: 'Save',
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',
    close: 'Close',
    ok: 'OK',
    yes: 'Yes',
    no: 'No',
    viewAll: 'View all',
    somethingWrong: 'Something went wrong',
    noConnection: 'No connection. What you already saved is safe.',
    requestId: 'Reference: {{id}}',
    empty: 'Nothing here yet',
    optional: 'optional',
  },
  nav: {
    home: 'Home',
    vessels: 'My vessels',
    queue: 'Queue',
    bills: 'Bills',
    payments: 'Payments',
    wallet: 'Wallet',
    announcements: 'Announcements',
    profile: 'Profile',
  },
  auth: {
    title: 'Sign in',
    subtitle: 'Enter the phone number registered with MIFCO',
    phoneLabel: 'Phone number',
    phonePlaceholder: '7XXXXXX',
    sendCode: 'Send code',
    sending: 'Sending…',
    codeSentTo: 'A 6-digit code was sent to {{phone}}',
    codeLabel: 'Enter the 6-digit code',
    verify: 'Verify and sign in',
    verifying: 'Verifying…',
    resendIn: 'Resend code in {{seconds}}s',
    resendNow: 'Resend code',
    changeNumber: 'Change number',
    invalidPhone: 'Enter the phone number the fisherman is registered with.',
    invalidCode: 'That code is not right. Check the SMS and try again.',
    codeExpired: 'This code has expired. Send a new one.',
    tooManyAttempts: 'Too many attempts. Wait a few minutes and try again.',
    signedOut: 'You have been signed out.',
    devOtpHint: 'Development only: the code 000000 always works.',
  },
  home: {
    greeting: 'Welcome back',
    walletBalance: 'Money in your wallet',
    pendingBillsNote: 'Bills awaiting confirmation are not counted in this balance yet.',
    goToWallet: 'View statement',
    todayQueue: "Today's queue",
    noActiveToken: 'You have no active queue token',
    joinQueue: 'Join a queue',
    waitingAhead: '{{count}} ahead of you',
    waitingAheadZero: "You're next",
    recentBills: 'Recent bills',
    noBills: 'No bills yet',
    announcementsUnread: '{{count}} unread announcement',
    announcementsUnreadPlural: '{{count}} unread announcements',
  },
  vessels: {
    title: 'My vessels',
    empty: 'No vessels are registered to you yet.',
    relationOwner: 'Owner',
    relationCrew: 'Crew',
    relationCrewRole: 'Crew · {{role}}',
    registration: 'Registration',
    island: 'Island',
    atoll: 'Atoll',
    capacity: 'Capacity',
    active: 'Active',
    inactive: 'Inactive',
  },
  queue: {
    title: 'Queue',
    joinTitle: 'Join a queue',
    collectionPoint: 'Collection point',
    selectCollectionPoint: 'Choose a collection point',
    vesselOptional: 'Vessel (optional)',
    join: 'Get a token',
    joining: 'Getting a token…',
    yourToken: 'Your token',
    tokenNumber: 'Token {{number}}',
    servingLabel: 'Now serving',
    servingNumber: 'Now serving {{number}}',
    servingNone: 'Not yet calling tokens',
    waitingAhead: '{{count}} boat ahead of you',
    waitingAheadPlural: '{{count}} boats ahead of you',
    waitingAheadZero: "You're next — get ready",
    status: {
      waiting: 'Waiting',
      called: 'Called — go to the counter',
      serving: 'Being served',
      done: 'Done',
      no_show: 'Missed',
    },
    issuedAt: 'Token issued at {{time}}',
    refreshing: 'Updating…',
    updatedAt: 'Updated {{time}}',
    joinAnother: 'Get another token',
    noVessels: 'You have no vessels on file — you can still get a token.',
  },
  bills: {
    title: 'My bills',
    filterAll: 'All',
    kind: {
      fish: 'Fish',
      ice: 'Ice',
      diesel: 'Diesel',
    },
    status: {
      draft: 'Awaiting confirmation',
      finalized: 'Confirmed',
      voided: 'Cancelled',
    },
    noNumberYet: 'Number issued once confirmed',
    empty: 'No bills yet',
    loadMore: 'Load more',
    number: 'Bill number',
    issuedAt: 'Issued',
    net: 'Total',
    viewDetail: 'View bill',
  },
  billDetail: {
    title: 'Bill',
    draftNotice:
      'MIFCO has not confirmed this bill yet. The amount below is not in your wallet balance, and the bill number is issued when the bill is confirmed.',
    gross: 'Before deductions',
    deductions: 'Deductions',
    net: 'Total',
    status: 'Status',
    issuedAt: 'Issued',
    download: 'Download PDF',
    downloading: 'Preparing PDF…',
    downloadFailed: 'Could not download the PDF. Try again.',
    notFound: 'This bill could not be found.',
  },
  payments: {
    title: 'Payment history',
    empty: 'No payments yet',
    loadMore: 'Load more',
    forBill: 'For bill {{number}}',
    method: {
      cash: 'Cash',
      bank_transfer: 'Bank transfer',
      wallet_offset: 'Paid from wallet',
    },
    paidAt: 'Paid',
  },
  wallet: {
    title: 'Wallet',
    balance: 'Current balance',
    asOf: 'As of {{time}}',
    statement: 'Statement',
    empty: 'No wallet activity yet',
    loadMore: 'Load more',
    balanceAfter: 'Balance after',
    debit: 'Money out',
    credit: 'Money in',
  },
  announcements: {
    title: 'Announcements',
    empty: 'No announcements right now',
    loadMore: 'Load more',
    unread: 'New',
    markingRead: 'Marking as read…',
  },
  profile: {
    title: 'Profile',
    name: 'Name',
    phone: 'Phone',
    address: 'Address',
    island: 'Island',
    atoll: 'Atoll',
    bankName: 'Bank',
    bankAccount: 'Bank account',
    edit: 'Edit profile',
    editTitle: 'Edit profile',
    save: 'Save changes',
    saving: 'Saving…',
    saved: 'Profile updated',
    bankChangeNotice:
      "You've asked to change your bank account. A staff member will review this before it takes effect — it will not change your bank account right away.",
    pendingBankChange: 'Bank account change pending review',
    pendingSince: 'Submitted {{time}}',
    status: {
      active: 'Active',
      suspended: 'Suspended',
      archived: 'Archived',
    },
    appearance: 'Appearance',
    theme: {
      label: 'Theme',
      light: 'Light',
      dark: 'Dark',
      system: 'Match device',
    },
    language: 'Language',
    languageDv: 'ދިވެހި',
    languageEn: 'English',
    logout: 'Sign out',
    confirmLogoutTitle: 'Sign out?',
    confirmLogoutBody: "You'll need your phone to sign in again.",
  },
  sportsPublic: {
    title: 'Sports fishing — purchase requirements',
    empty: 'No open purchase requirements right now',
    remainingLabel: 'Remaining',
    remainingKg: '{{kg}} remaining',
    targetKg: 'Target {{kg}}',
    targetLabel: 'Target',
    rate: 'Rate {{amount}} per kg',
    rateLabel: 'Rate',
    window: '{{from}} – {{to}}',
    status: {
      draft: 'Not yet open',
      published: 'Open',
      closed: 'Closed',
    },
    collectionPoint: 'Collection point',
    viewDetail: 'View details',
    register: 'Register for sports fishing',
    nationalIdLabel: 'National ID',
    nationalIdPlaceholder: 'A123456',
    continue: 'Continue',
    notFound: 'No fisherman is registered with that national ID.',
    otpSentTo: 'A code was sent to {{phone}}',
    activated: "You're registered for sports fishing.",
    signInInstead: 'Already registered? Sign in to the portal',
  },
  collectionPoint: {
    title: 'Collection point',
    atoll: 'Atoll',
    island: 'Island',
    type: 'Type',
    inactive: 'Currently not operating',
  },
  verify: {
    title: 'Receipt verification',
    subtitle: 'Scan a MIFCO receipt QR code to check it here',
    number: 'Bill number',
    issuedAt: 'Issued',
    vessel: 'Vessel',
    total: 'Total amount',
    valid: 'This receipt is genuine',
    pending: 'This receipt is on record, awaiting confirmation',
    pendingNote:
      'MIFCO has this collection on record and has not confirmed it yet. The amount above is what was weighed, not money that is payable: no bill number has been issued and nothing has been credited to the wallet. Keep the slip and check again after the collection point confirms it.',
    reversed: 'Reversed',
    reversedAt: 'Reversed on',
    reversedNote: 'This collection was recorded and confirmed, and has since been reversed. The amount shown is no longer owed and is not in your wallet balance. Ask at the collection point if you were not expecting this.',
    reference: 'Reference',
    notFound: 'No receipt matches this code. It may be mistyped or invalid.',
    unavailable: 'This receipt could not be checked right now. Check your connection and try again — this does not mean the receipt is invalid.',
    invalidCode: 'This code could not be read.',
  },
  errors: {
    network_error: 'No connection. Check your signal and try again.',
    timeout: 'That took too long. Try again.',
    aborted: 'Cancelled.',
    malformed_response: 'The server sent something unexpected. Try again.',
    validation_failed: 'Please check the highlighted fields.',
    not_found: 'Not found.',
    conflict: 'This has already changed — refresh and try again.',
    unauthenticated: 'Please sign in again.',
    forbidden: "You don't have access to this.",
    rate_limited: 'Too many attempts. Wait a moment and try again.',
    otp_invalid: 'That code is not right.',
    otp_expired: 'This code has expired.',
    service_unavailable: 'The service is temporarily unavailable.',
    internal_error: 'Something went wrong on our end.',
    generic: 'Something went wrong. Try again.',
  },
  notFound: {
    title: 'Page not found',
    body: "The page you're looking for doesn't exist.",
    goHome: 'Go to home',
  },
}

export default en
export type MessageTree = typeof en
